import React, { useContext, useEffect, useState } from 'react'
import axios from 'axios'
import StockContext from '../../../../contexts/StockContext'
import TableFiltersContext from '../../../../contexts/TableFiltersContext'
import ModalContext from '../../../../contexts/ModalContext'
import UserContext from '../../../../contexts/UserContext'
import editPen from '../../../../assets/edit-pen.svg'
import lookup from '../../../../assets/certificates/Tables/lookup.svg'
import CertificatesContext from '../../../../contexts/CertificatesContext'
import SectionContext from '../../../../contexts/SectionContext'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import ApiService from '../../../../services/ApiService'

import {
  ConfigButton,
  DivInTd,
  GlobalTable,
  HeaderLine,
  Td,
  TdButtons,
  Th,
  Tr,
  FooterTr,
} from '../../../../components/styles/table-style'
import { useOutletContext } from 'react-router-dom'
import { StockOutletType } from '..'
import { TypeMainOutlet } from '../..'
import { Settings } from 'lucide-react'

interface Stock {
  id: number
  codeId: number
  material: string
  certificateId: number
  format: string
  finishing: string
  fabrication: string
  heatTreatment: string
  ncm: string
  originCountry: string
  supplier: string
  stockStreet: string
  shelf: string
  stockLocation: string
  quantity: string
  totalWeight: string
  dimension1: string
  dimension2: string
  lengthInMm: string
  costPerKg: string
  costPerMm: string
  code: string
  invoice: any[]
  totalCost: string
  density: string
  certificates: any[]
  availableBalance: string
  measurementUnit: string
  totalCmc: string
  unitCmc: string
  trackingNumber: string
  family: string
  description: string
  stockStatus: string
  characteristic: string
  receivedDate: string
  purchaseOrder: string
  nf: string
  certificateNumber: string
  heatNumber: any 
  //{certificates:any[]}
  createdAt: string
  updatedAt: string
  movimentType: string
  observation: string
  unitOfMeasurement: string
  isActivated: boolean
}

const StockTable: React.FC = () => {
  const [permissions, setPermissions] = useState<string[]>([])
  const {
    stockQuantity,
    details,
    setStock,
    stockList,
    updateStockList,
    refresh,
    hasMoreStock,
    isLoading,
  } = useContext(StockContext)
  const { stockFilters, setStockFilters } = useContext(TableFiltersContext)
  const { setCertificate } = useContext(CertificatesContext)
  const { search } = useOutletContext<TypeMainOutlet>()
  const { setModalActive, setModalSection, setItIsUpdate } =
    useContext(ModalContext)
  const { user } = useContext(UserContext)
  const {
    setTabIndex,
    fields,
    setIsDropdownOpen,
    setQuantity,
    quantity,
  } = useOutletContext<StockOutletType>()
  const { setStockSection } = useContext(SectionContext)

  useEffect(() => {
    setStockFilters([]);
    setStockSection('stock');
  }, [setStockFilters, setStockSection]);

  const [totalStockCount, setTotalStockCount] = useState<number | undefined>(undefined)

  useEffect(() => {
    let mounted = true
    const fetchTotal = async () => {
      try {
        const resp = await ApiService.stock.getTotalStock()
        if (mounted && resp && resp.data != null) {
          const value = typeof resp.data === 'number' ? resp.data : resp.data.totalItems ?? resp.data.total ?? undefined
          setTotalStockCount(value)
        }
      } catch (e) {
        console.error(e)
      }
    }

    fetchTotal()

    return () => {
      mounted = false
    }
  }, [stockFilters, search, refresh])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateStockList(quantity, stockFilters, search)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [quantity, stockFilters, search, refresh])

  useEffect(() => {
    const fetchPermissions = async () => {
      const perms = await getPermissions()
      setPermissions(perms)
    }

    if (user.profileId) {
      fetchPermissions()
    }
  }, [user.profileId])

  const formatNumber = (num: number) => {
    return num.toFixed(2)
  }

  const handleCheckCertificate = async (certificateId: number) => {
    const certificateUrl = `${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates`
    axios
      .get(`${certificateUrl}/certificate/${certificateId}`)
      .then((response) => {
        setModalActive(true)
        setModalSection('viewCertificate')
        setCertificate(response.data)
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const filterStockData = (stock: Stock): Partial<Stock> => {
    const allowedFields = [
      'id',
      'codeId',
      'codeId',
      'code',
      'material',
      'format',
      'finishing',
      'fabrication',
      'heatTreatment',
      'originCountry',
      'supplier',
      'stockStreet',
      'shelf',
      'stockLocation',
      'ncm',
      'quantity',
      'totalWeight',
      'dimension1',
      'dimension2',
      'lengthInMm',
      'costPerKg',
      'costPerMm',
      'totalCost',
      'invoice',
      'density',
      'certificates',
      'availableBalance',
      'totalCmc',
      'trackingNumber',
      'receivedDate',
      'purchaseOrder',
      'nf',
      'certificateNumber',
      'heatNumber',
      'heatNumber',
      'family',
      'description',
      'stockStatus',
      'characteristic',
      'measurementUnit',
      'isActivated',
    ] as const

    const filtered: any = {}
    allowedFields.forEach((key) => {
      if (key in stock) {
        filtered[key] = stock[key] ?? ''
      }
    })
    return filtered
  }

  const convertSupplierToString = (item: any) => {
    const newItem = { ...item }
    if (typeof item.supplier === 'object' && item.supplier !== null) {
      newItem.supplier = item.supplier?.name || ''
    }
    return newItem
  }

  const handleUpdateClick = (item: Stock) => {
    const filteredItem = filterStockData(item);
    const convertedSupplier = convertSupplierToString(filteredItem);
    setStock(convertedSupplier);
    setItIsUpdate(true);
    setModalActive(true);
    setModalSection('firstCustomAdd');
  };

  const handleEntryOrExit = (item: Stock) => {
    setStock(item);
    setModalActive(true);
    setModalSection('modalEntryOrExit');
  };

  const handleNewModal = (item: any) => {
    setStock(item)
    setModalActive(true)
    setModalSection('newModal')
  }

  const getPermissions = async () => {
    try {
      const response = await axios(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/profile/find/${user.profileId}`,
      )

      if (response.data) {
        return response.data.permissions
      }
    } catch (error: any) {
      console.log('Usuário sem perfil')
    }
  }

  const TableComponent: React.FC = () => {
    return (
      <GlobalTable className='short'>
        <thead>
          <HeaderLine>
            {fields.whichFieldsToShow
              .filter((field) => field.checked)
              .map((field, index) => (
                <Th key={index}>{field.fieldName}</Th>
              ))}
            <Th></Th>
            <ConfigButton onClick={() => setIsDropdownOpen((prev) => !prev)}>
              <Settings />
            </ConfigButton>
            <Th></Th>
            <ConfigButton onClick={() => setIsDropdownOpen((prev) => !prev)}>
              <Settings />
            </ConfigButton>
          </HeaderLine>
        </thead>
        <tbody>
          {stockList.map((item, itemIndex) => (
            <Tr
              key={item.code}
              className={itemIndex === 0 ? 'first' : ''}
              style={{
                backgroundColor:
                  item.isActivated === false ? '#FFFCEE' : '#fff',
                color: item.isActivated === false ? '#9d7d07' : '#000',
              }}
            >
              {fields.whichFieldsToShow
                .filter((field) => field.checked)
                .map((fieldObj, index) => (
                  <Td key={index}>
                    {fieldObj.field === 'description' ? (
                      <span
                        onClick={() =>
                          navigator.clipboard.writeText(
                            item[fieldObj.field as keyof typeof item] as string,
                          )
                        }
                        style={{
                          cursor: 'pointer',
                        }}
                        title='Clique para copiar'
                      >
                        {item[fieldObj.field as keyof typeof item]}
                      </span>
                    ) : fieldObj.field === 'lengthInMm' ||
                      fieldObj.field === 'totalWeight' ? (
                      formatNumber(
                        parseFloat(
                          item[fieldObj.field as keyof typeof item] as string,
                        ),
                      )
                    ) : (
                      (item[fieldObj.field as keyof typeof item] as any)?.name ||
                      item[fieldObj.field as keyof typeof item]
                    )}
                  </Td>
                ))}
              <TdButtons>
                <img
                  src={lookup}
                  alt='look up icon'
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleNewModal(item)}
                /* className={!stockList[itemIndex].certificateId ? 'withoutCertificate' : ''}
                onClick={() => {
                  if(stockList[itemIndex].heatNumber.certificate.length > 0)
                    handleCheckCertificate(stockList[itemIndex].heatNumber.certificate[0].id)
                  if(stockList[itemIndex].heatNumber.certificate.length > 0)
                    handleCheckCertificate(stockList[itemIndex].heatNumber.certificate[0].id)
                  return null
                }}
                title={stockList[itemIndex].certificateId ? 'Ver certificado' : ''} */
                />
              </TdButtons>
            </Tr>
          ))}
        </tbody>
        <tfoot>
          <FooterTr>
            <Td colSpan={fields.whichFieldsToShow.filter((f) => f.checked).length + 2} style={{ textAlign: 'center', background: 'transparent', padding: '1rem 0' }}>
              <LoadMoreButton
                onLoadMore={() => setQuantity((prev) => prev + 10)}
                currentCount={stockList ? stockList.length : 0}
                totalCount={typeof totalStockCount === 'number' ? totalStockCount : undefined}
                hasMore={hasMoreStock}
                isLoading={isLoading}
              />
            </Td>
          </FooterTr>
        </tfoot>
      </GlobalTable>
    );
  };

  let tableClass = '';
  if (stockFilters.length !== 0) {
    if (details) {
      tableClass = 'filteredTable';
    } else {
      tableClass = 'filteredTable shortTable';
    }
  } else {
    if (!details) {
      tableClass = 'shortTable';
    }
  }

  return <TableComponent />
}

export default StockTable;